import { Component, EventEmitter, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from './app.state';
import { Observable } from 'rxjs/Observable';
import { NavigationEnd, Router } from '@angular/router';
import { selectAppReady } from './core/core.selector';
import { SourceAppService } from './core/services/source-app.service';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';

import '../style/app.scss';
import { animate, AnimationEvent, group, state, style, transition, trigger } from '@angular/animations';
import * as auth from './core/actions/logout.actions';
import { Globals } from './app.globals';
import { TranslateService } from '@ngx-translate/core';

/**
 * Main App Component.
 *
 * Dispatches requests to load config and contracts.
 */
@Component({
    selector: 'ssf-app',
    template: require('./app.component.html'),
    styles: [require('./app.component.scss')],
    animations: [
        trigger('slideInOut', [
            state('void', style({
                'transform': 'translateY(-100%)'
            })),
            transition(':enter', [group([
                    animate('800ms ease-in-out', style({'transform': 'translateY(0)'}))
                ]
            )]),
            transition(':leave', [group([
                    animate('800ms ease-in-out', style({'transform': 'translateY(-100%)'}))
                ]
            )]),
        ]),

        trigger('fadeInOut', [
            state('void', style({
                'opacity': '0'
            })),
            transition(':enter', [group([
                    animate('800ms ease-in-out', style({'opacity': '1'}))
                ]
            )]),
            transition('* => out', [group([
                    animate('700ms ease-in-out', style({'opacity': '0'}))
                ]
            )]),
        ])
    ]
})
export class AppComponent {
    public showHeader = false;
    public appReady$: Observable<boolean>;
    // if true, there is an ongoing log-out process
    public loggingOut = false;
    public animationState = 'in';
    public bodyVisible = true;

    // variables used for idle
    idleState = '';
    timedOut = false;
    lastPing?: Date = null;
    showIdle = false;
    @Output() logout = new EventEmitter<any>();

    constructor(private store: Store<AppState>, private router: Router, public sourceService: SourceAppService,
                private idle: Idle, private keepalive: Keepalive, private translate: TranslateService) {

        idle.setIdle(Globals.idleLogout.idle);
        idle.setTimeout(Globals.idleLogout.timeout);
        idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

        idle.onIdleEnd.subscribe(() => {
            this.showIdle = false;
            this.idleState = '';
        });
        idle.onTimeout.subscribe(() => {
            this.idleState = this.translate.instant('APP.IDLE.IDLE-LOGGED-OUT');
            this.timedOut = true;
            this.logout.emit(null);

            // logout causes redirect so we need some time for the animations to run
            setTimeout(() => {
                this.store.dispatch(new auth.LogoutAction());
            }, 500);
        });
        idle.onIdleStart.subscribe(() => {
            this.showIdle = false;
            this.idleState = '';
        });
        idle.onTimeoutWarning.subscribe((countdown) => {
            this.showIdle = true;
            this.idleState = this.translate.instant('APP.IDLE.IDLE-WARNING-1') + countdown + this.translate.instant('APP.IDLE.IDLE-WARNING-2');
        });
        this.keepalive.interval(Globals.idleLogout.ping);

        this.keepalive.onPing.subscribe(() => this.lastPing = new Date());

        this.reset();

        this.appReady$ = this.store.select(selectAppReady);

        this.router.events.filter(e => e instanceof NavigationEnd).subscribe(route => {
            const url = (route as NavigationEnd).url;
            this.showHeader = url.indexOf('expired') === -1  &&  url.indexOf('401') === -1
            && url.indexOf('logout') === -1;
        });
    }

    onLogout() {
        this.loggingOut = true;
        this.animationState = 'out';
    }

    onAnimationDone(event: AnimationEvent) {
        if (event.toState === 'out') {
            this.bodyVisible = false;
        }
        if (this.sourceService.isFromCps()) {
            this.loggingOut = false;
        }
    }

    reset() {
        this.idle.watch();
        this.idleState = '';
        this.showIdle = false;
        this.timedOut = false;
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/angular-router-loader/src!./~/tslint-loader!./src/app/app.component.ts